import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { songsApi } from '../api/songs';
import { toast } from 'sonner';
import offlineDB from '../utils/offlineDB';

// Query keys
export const songKeys = {
  all: ['songs'],
  detail: (id) => ['songs', id],
  denonCount: ['songs', 'denon-count'],
  duplicates: ['songs', 'duplicates'],
};

// Helper function to filter songs locally (for offline mode)
const filterSongsLocally = (songs, filters, page, limit) => {
  let filtered = [...songs];
  
  // Apply search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(song => 
      song.title?.toLowerCase().includes(searchLower) ||
      song.artist?.toLowerCase().includes(searchLower) ||
      song.genero?.toLowerCase().includes(searchLower)
    );
  }
  
  // Apply genre filter
  if (filters.genre && filters.genre !== 'all') {
    filtered = filtered.filter(song => song.genero === filters.genre);
  }
  
  // Apply rating filter
  if (filters.rating && filters.rating !== 'all') {
    const ratingNum = parseInt(filters.rating);
    filtered = filtered.filter(song => song.rating === ratingNum);
  }
  
  // Apply momento filter
  if (filters.momento && filters.momento !== 'all') {
    filtered = filtered.filter(song => song.momento === filters.momento);
  }
  
  // Apply energia filter
  if (filters.energia && filters.energia !== 'all') {
    filtered = filtered.filter(song => song.energia === filters.energia);
  }
  
  // Apply decade filter
  if (filters.decade && filters.decade !== 'all') {
    filtered = filtered.filter(song => song.decade === filters.decade);
  }
  
  // Apply favorito filter
  if (filters.favorito) {
    filtered = filtered.filter(song => song.favorito === true);
  }
  
  // Apply pendiente_denon filter
  if (filters.pendiente_denon) {
    filtered = filtered.filter(song => song.pendiente_denon === true);
  }
  
  // Calculate pagination
  const total = filtered.length;
  const totalPages = Math.ceil(total / limit);
  const startIndex = (page - 1) * limit;
  const paginatedData = filtered.slice(startIndex, startIndex + limit);
  
  return {
    data: paginatedData,
    pagination: {
      total,
      page,
      limit,
      total_pages: totalPages,
      has_next: page < totalPages,
      has_prev: page > 1
    }
  };
};

// Helper to extract unique filter values from cached songs
const extractFilterValues = (songs) => {
  const genres = [...new Set(songs.map(s => s.genero).filter(Boolean))].sort();
  const momentos = [...new Set(songs.map(s => s.momento).filter(Boolean))].sort();
  const energia = [...new Set(songs.map(s => s.energia).filter(Boolean))].sort();
  const ratings = [...new Set(songs.map(s => s.rating).filter(r => r != null))].sort((a, b) => b - a);
  
  return { genres, momentos, energia, ratings };
};

// Get paginated songs with filters - with offline fallback
export const useSongs = (page = 1, limit = 20, filters = {}, enabled = true) => {
  // Serialize filters for stable query key
  const filtersKey = JSON.stringify(filters);
  
  return useQuery({
    queryKey: [...songKeys.all, page, limit, filtersKey],
    queryFn: async () => {
      // Check if we have a token
      const token = localStorage.getItem('token');
      
      // If no token, return empty
      if (!token) {
        console.log('[useSongs] No token, returning empty');
        return {
          data: [],
          pagination: { total: 0, page: 1, limit, total_pages: 0, has_next: false, has_prev: false }
        };
      }
      
      // Try online first
      if (navigator.onLine) {
        try {
          console.log('[useSongs] Fetching from API with filters:', JSON.stringify(filters));
          const result = await songsApi.getAll(page, limit, filters);
          console.log(`[useSongs] Got ${result?.data?.length || 0} songs, total: ${result?.pagination?.total || 0}`);
          return result;
        } catch (error) {
          console.warn('[useSongs] API failed:', error.message);
        }
      }
      
      // Offline fallback
      const cachedSongs = await offlineDB.getAllSongs();
      if (cachedSongs && cachedSongs.length > 0) {
        return filterSongsLocally(cachedSongs, filters, page, limit);
      }
      
      return {
        data: [],
        pagination: { total: 0, page: 1, limit, total_pages: 0, has_next: false, has_prev: false }
      };
    },
    staleTime: 0, // Always stale - forces refetch
    gcTime: 0, // Don't cache at all - this should fix the issue with empty results being cached
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    enabled: true,
    retry: 2,
  });
};

// Get ALL songs without pagination (for special operations like duplicates, export) - with offline fallback
export const useAllSongs = (enabled = true) => {
  return useQuery({
    queryKey: ['songs', 'all'],
    queryFn: async () => {
      // Try online first
      if (navigator.onLine) {
        try {
          return await songsApi.getAllNoPagination();
        } catch (error) {
          console.warn('[useAllSongs] API failed, falling back to offline:', error.message);
        }
      }
      
      // Offline fallback
      console.log('[useAllSongs] Loading all songs from cache...');
      const cachedSongs = await offlineDB.getAllSongs();
      return cachedSongs || [];
    },
    staleTime: 1000 * 60 * 5,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
    retry: (failureCount, error) => {
      if (!navigator.onLine) return false;
      return failureCount < 1;
    },
  });
};

// Get all unique filter values (for filter dropdowns) - with offline fallback
export const useFilterValues = (enabled = true) => {
  return useQuery({
    queryKey: ['songs', 'filter-values'],
    queryFn: async () => {
      // Check for token
      const token = localStorage.getItem('token');
      
      // Try online first
      if (navigator.onLine && token) {
        try {
          return await songsApi.getFilterValues();
        } catch (error) {
          console.warn('[useFilterValues] API failed, falling back to offline:', error.message);
        }
      }
      
      // Offline fallback - extract from cached songs
      console.log('[useFilterValues] Extracting filter values from cache...');
      const cachedSongs = await offlineDB.getAllSongs();
      
      if (cachedSongs && cachedSongs.length > 0) {
        return extractFilterValues(cachedSongs);
      }
      
      return { genres: [], momentos: [], energia: [], ratings: [] };
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    enabled: true, // Always enabled
    retry: (failureCount, error) => {
      if (!navigator.onLine) return false;
      return failureCount < 2;
    },
  });
};

// Get song by ID
export const useSong = (songId) => {
  return useQuery({
    queryKey: songKeys.detail(songId),
    queryFn: () => songsApi.getById(songId),
    enabled: !!songId,
  });
};

// Get Denon pendientes count
export const useDenonPendientesCount = () => {
  return useQuery({
    queryKey: songKeys.denonCount,
    queryFn: songsApi.getDenonPendientesCount,
  });
};

// Create song mutation
export const useCreateSong = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: songsApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: songKeys.all });
      toast.success('Canción añadida correctamente');
      return data;
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al añadir canción');
    },
  });
};

// Update song mutation
export const useUpdateSong = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: songsApi.update,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: songKeys.all });
      queryClient.invalidateQueries({ queryKey: songKeys.detail(variables.songId) });
      toast.success('Canción actualizada');
      return data;
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al actualizar canción');
    },
  });
};

// Delete song mutation
export const useDeleteSong = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: songsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: songKeys.all });
      toast.success('Canción eliminada');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al eliminar canción');
    },
  });
};

// Create Denon playlist mutation
export const useCreateDenonPlaylist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: songsApi.createDenonPlaylist,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: songKeys.all });
      queryClient.invalidateQueries({ queryKey: songKeys.denonCount });
      return data;
    },
  });
};

// Detect duplicates
export const useDetectDuplicates = () => {
  return useQuery({
    queryKey: songKeys.duplicates,
    queryFn: songsApi.detectDuplicates,
    enabled: false, // Only run when explicitly called
  });
};
